import { Feature, GeoJsonProperties, Polygon } from "geojson";
import { LngLat, PointLike } from "mapbox-gl";
import { MapRef } from "react-map-gl";
import api from "../api/api";
import { countyFillLayerId } from "../components/mapLayers/CountiesMapLayer";
import { regridZoningInfoLayerId } from "../components/mapLayers/RegridZoningMapLayer";
import { stateShapesLayerId } from "../components/mapLayers/StateShapesMapLayer";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import IIndustrialScarcityResponse from "../interfaces/IIndustrialScarcityResponse";
import IParcel from "../interfaces/IParcel";
import IParcelResponse from "../interfaces/IParcelResponse";
import IZoningLayer from "../interfaces/IZoningLayer";
import usStateAbbrev from "../utils/usStateAbbrev";

export default class ParcelApiService {
    public async getParcelInfos(coordinates: LngLat[]): Promise<IParcel[]> {
        const response = await api.post<IParcelResponse[]>("/parcel/getinfo", {
            coordinates: coordinates.map(c => ({ latitude: c.lat, longitude: c.lng })),
        });

        return response.data.map(item => this.mapToParcel(item));
    }

    public async getIndustrialScarcityRatio(shape: Feature<Polygon, GeoJsonProperties>): Promise<number> {
        const response = await api.post<IIndustrialScarcityResponse>("/parcel/calculateIndustrialScarcity", { shape });
        return response.data.ratio;
    }

    public async getCountyShapeFeature(point: IGeoCoordinate): Promise<Feature<Polygon>> {
        const response = await api.get<Feature<Polygon>>(`/parcel/getCountyShape?latitude=${point.latitude}&longitude=${point.longitude}`);
        return response.data;
    }

    public async getMunicipalityShapeFeature(point: IGeoCoordinate, municipality: string): Promise<Feature<Polygon>> {
        const response = await api.get<Feature<Polygon>>(`/parcel/getMunicipalityShape?latitude=${point.latitude}&longitude=${point.longitude}&municipality=${encodeURIComponent(municipality)}`);
        return response.data;
    }

    private mapToParcel(response: IParcelResponse): IParcel {
        return {
            id: response.regridId ?? "",
            coordinate: response.coordinate,
            shape: { points: response.shape },
            taxLink: response.taxLink,
            zoningLink: response.zoningLink,
            deedsLink: response.deedsLink,
            permitLink: response.permitLink,
            streetAddress: response.streetAddress,
            city: response.city,
            zipCode: response.zipCode,
            county: response.county,
            municipality: "",
            state: response.state,
            submarket: response.submarket,
            parcelId: response.parcelId,
            buildingSize: response.buildingSize,
            landSize: response.landSize && Number(response.landSize.toFixed(2)),
            currentFar: response.currentFar,
            zoningType: response.zoningType,
            salePrice: response.salePrice,
            saleDate: response.saleDate,
            saleType: response.saleType,
            ownerEntity: this.toTitleCase(response.ownerEntity),
            ownerAddress: this.toTitleCase(response.ownerAddress)?.replace(/  +/g, ' ') ?? null,
            sellerName: response.sellerName,
            sellerAddress: response.sellerAddress,
            priceSFBuilding: response.priceSFBuilding,
            priceSFLand: response.priceSFLand,
            priceAcreLand: response.priceAcreLand,
            buildingType: this.toTitleCase(response.buildingType),
            yearBuilt: response.yearBuilt,
            yearRenovated: response.yearRenovated,
            numberOfUnits: response.numberOfUnits,
            condos: response.condos,
            numberOfFloors: response.numberOfFloors,
            parkingSpaces: response.parkingSpaces,
            landAssessment: response.landAssessment,
            buildingAssessment: response.buildingAssessment,
            totalAssessment: response.totalAssessment,
            numberOfBuildings: response.numberOfBuildings,
            typeOfStructure: response.typeOfStructure,
            siteDescription: response.siteDescription,
            ownershipStructure: response.ownershipStructure,
            annualTaxBill: response.annualTaxBill,
            taxYear: response.taxYear,
            zoning: response.zoning,
            zoningDescription: response.zoningDescription,
            zoningSubtype: response.zoningSubtype,
            zoningObjective: response.zoningObjective,
            zoningCodeLink: response.zoningCodeLink,
            permittedLandUses: response.permittedLandUses ?? null,
            permittedLandUsesAsOfRight: response.permittedLandUsesAsOfRight ?? null,
            permittedLandUsesConditional: response.permittedLandUsesConditional ?? null,
            femaFloodZone: response.femaFloodZone,
            femaFloodZoneSubtype: response.femaFloodZoneSubtype,
            gradeChangeFeet: response.gradeChangeFeet,
            distanceToPowerFeet: response.distanceToPowerFeet,
            femaRiskRating: response.femaRiskRating,
            medianHouseholdIncome: response.medianHouseholdIncome,
            householdIncomeGrowthNext5Years: response.householdIncomeGrowthNext5Years,
            housingAffordabilityIndex: response.housingAffordabilityIndex,
            populationDensity: response.populationDensity,
            populationGrowthPast5Years: response.populationGrowthPast5Years,
            populationGrowthNext5Years: response.populationGrowthNext5Years,
            housingGrowthPast5Years: response.housingGrowthPast5Years,
            housingGrowthNext5Years: response.housingGrowthNext5Years,
        };
    }

    public getCountyName(map: MapRef, point: LngLat): string {
        const center = map.project(point);
        const features = map.queryRenderedFeatures(center, {
            layers: [countyFillLayerId]
        });

        return features[0] ? features[0].properties!.NAME : "";
    }

    public getMunicipalityName(map: MapRef, point: LngLat): string {
        const center = map.project(point);
        const features = map.queryRenderedFeatures(center, {
            layers: [regridZoningInfoLayerId]
        });

        return features[0] ? features[0].properties!.municipality_name : "";
    }

    public getStateAbbrevByPoint(map: MapRef, point: LngLat): string {
        const mapboxPoint = map.project([point.lng, point.lat]);
        const features = map.queryRenderedFeatures(mapboxPoint, { layers: [stateShapesLayerId] });
        return features[0] ? usStateAbbrev[features[0].properties!.NAME] : "";
    }

private toTitleCase = (str: string | null): string | null => {
        return str
            ? str
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            : null;
    };

    public getZoningType(map: MapRef, countyName: string, lngLat: LngLat): string | null {
        const center = map.project(lngLat);

        const bbox = [
            [center.x - 2, center.y - 2],
            [center.x + 2, center.y + 2],
        ] as [PointLike, PointLike];

        const zoningLayer = this.getZoningTypeByCounty(countyName);

        if (!zoningLayer)
            return null;

        const features = map.queryRenderedFeatures(bbox, {
            layers: [zoningLayer.layerName],
        });

        if (!features || !features.length || !features[0].properties)
            return null;

        return zoningLayer.fields.map((field: string) => features[0].properties![field]).join(" - ");
    };

    private getZoningTypeByCounty(countyName: string): IZoningLayer | null {
        switch (countyName.toLowerCase()) {
            case 'district of columbia':
                return { layerName: "Zoning - DC filling", fields: ["ZONING_LAB", "ZONE_DISTR"] };
            case 'arlington':
                return { layerName: "Zoning - Arlington filling", fields: ["ZN_DESIG", "LABEL"] };
            case 'alexandria city':
                return { layerName: "Zoning - Alexandria filling", fields: ["ZONING"] };
            case 'alexandria':
                return { layerName: "Zoning - Alexandria filling", fields: ["ZONING"] };
            case 'fairfax':
                return { layerName: "Zoning - Fairfax filling", fields: ["ZONECODE", "ZONETYPE"] };
            case 'montgomery':
                return { layerName: "Zoning - Montgomery filling", fields: ["LONGZONE"] };
            case 'prince george\'s':
                return { layerName: "Zoning - Prince Georges filling", fields: ["CLASS"] };
            default:
                return null;
        }
    };

    public getZoningNameByPoint(map: MapRef, point: LngLat): string {
        let mapboxPoint = map.project([point.lng, point.lat]);
        const zoningLayerIds = [regridZoningInfoLayerId];

        let features = map.queryRenderedFeatures(mapboxPoint, { layers: zoningLayerIds });
        if (features.length === 0) {
            map.jumpTo({ center: point });
            mapboxPoint = map.project([point.lng, point.lat]);
            features = map.queryRenderedFeatures(mapboxPoint, { layers: zoningLayerIds });
        }

        return features[0] ? features[0].properties!.zoning : "";
    }
}
